import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterStand, Startfilter } from '@xxtract/tabelfilters';
import type { DashboardData } from '../completeness.api';
import { ProductRow } from './ProductRow';
import { FieldTypeFilter } from './FieldTypeFilter';
import { CategoryTabs } from './CategoryTabs';
import { useProducts } from '../hooks/useCompleteness';
import {
  COMPLEETHEID,
  ordenMerken,
  PRODUCT_LIJST,
  paginafilterParameters,
  paginafilterWaarde,
  productenSleutels,
  ZICHTBARE_KOLOMMEN,
  zonderVerborgenFacetten,
} from '../productLijst';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { SkeletonTable } from '@/components/feedback/SkeletonCard';
import { FOCUS_CLASS } from '@/components/tabel/stijl';
import { ariaSort, Filterbalk, SorteerbareKop, useServerTabelData, useTabelStand, type LinkWeigering, type ServerAntwoord } from '@/components/tabel';

/** Technische sleutels in constanten, zodat de i18n-lintregel ze niet als schermtekst leest. */
const FILTER_TEST_ID = 'products';
const PF = { veldtype: 'fieldType', categorie: 'category' } as const;
const PAGINAGROOTTES = [25, 50, 100, 250, 500];
const STANDAARD_PAGINAGROOTTE = PAGINAGROOTTES[0]!;
const COMPLEETHEID_LABELS: Record<string, string> = {
  all: 'products.allProducts',
  incomplete: 'products.incompleteOnly',
  complete: 'products.completeOnly',
};

const KOP_STIJL = {
  backgroundColor: 'var(--background-muted)',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--foreground-muted)',
  letterSpacing: '0.04em',
  borderBottom: '1px solid var(--border-soft)',
} as const;
const KOP_BREEDTE: Record<string, string> = { categorie: '170px', missingFields: '110px' };

const knopStijl = (actief: boolean) => ({
  backgroundColor: actief ? 'var(--primary)' : 'var(--background)',
  color: actief ? '#fff' : 'var(--foreground-muted)',
});

interface ProductListProps {
  clientId?: string;
  /** Categorietabs boven de filterbalk (paginafilter `category`: onvolledig in die categorie). */
  categories?: DashboardData['categories'];
  /** Startfilters uit een binnenkomende link (`useStartadres` op de pagina), per standsleutel. */
  start?: Record<string, Startfilter | undefined>;
  /** Ongeldige waarden uit die link (`FilterRejectedMessage`). */
  linkGeweigerd?: readonly LinkWeigering[];
  onLinkGewist?: () => void;
}

/**
 * spec-tabelfilters story 9: productenlijst als serverlijst in het geheugen (§8). Kolomfilters,
 * gestapelde sortering, pagina en paginagrootte staan in de lijststand `producten:<klant>`;
 * compleetheid, veldtype, categorietab en vast merk in `productenPagina:<klant>`. Aandacht is een
 * verborgen kolomfilter (besluit Friso 27-09-2026): geen control, alleen de reset wist hem.
 */
export function ProductList({ clientId, categories = [], start, linkGeweigerd, onLinkGewist }: ProductListProps) {
  const { t } = useTranslation();
  const lijstRef = useRef<HTMLDivElement>(null);
  const sleutels = productenSleutels(clientId);

  const bewaardePaginafilters = useLijstStandStore((s) => s.standen[sleutels.pagina]?.filters);
  const zetPaginafilter = useLijstStandStore((s) => s.zetPaginafilter);
  const paginafilters: FilterStand = start?.[sleutels.pagina]?.filters ?? bewaardePaginafilters ?? {};
  const zetPf = (sleutel: string, waarde: string | null) => {
    const { [sleutel]: _oud, ...rest } = paginafilters;
    zetPaginafilter(sleutels.pagina, waarde ? { ...rest, [sleutel]: [waarde] } : rest);
  };

  const stand = useTabelStand(PRODUCT_LIJST, sleutels.lijst, {
    start: start?.[sleutels.lijst],
    afhankelijkVan: sleutels.pagina,
    paginagrootte: STANDAARD_PAGINAGROOTTE,
  });
  // Alleen een paginagrootte die de keuzelijst kent: een link met `pageSize=1000` gaf anders steeds een 400 (API-max 500).
  const paginagrootte = PAGINAGROOTTES.includes(stand.paginagrootte ?? 0) ? stand.paginagrootte! : STANDAARD_PAGINAGROOTTE;
  const context = useMemo(() => ({ t: (sleutel: string) => t(sleutel) }), [t]);
  const standaard = PRODUCT_LIJST.standaardSortering?.(context);

  // De parameters hangen aan het vorige antwoord (totaal voor de laatste pagina, suggesties); dat
  // antwoord komt na de vraag binnen, dus het gaat via de stand een render later de hook in.
  const [antwoord, setAntwoord] = useState<ServerAntwoord>();
  const tabel = useServerTabelData(PRODUCT_LIJST, { ...stand, paginagrootte }, antwoord);
  const { data, isLoading, isFetching, isError, error, refetch } = useProducts({
    clientId,
    parameters: { ...zonderVerborgenFacetten(tabel.parameters), ...paginafilterParameters(paginafilters) },
  });
  const meta = data?.meta;
  useEffect(() => {
    if (meta) setAntwoord({ totaal: meta.total, facetten: meta.facetten, suggesties: meta.suggesties, afgekapt: meta.afgekapt });
  }, [meta]);

  if (isLoading) return <SkeletonTable rows={8} />;

  const products = data?.data ?? [];
  const compleetheid = paginafilterWaarde(paginafilters, 'completeness');

  const paginafilterRij = (
    <>
      <div className="flex rounded-lg" data-testid="completeness-filter" style={{ border: '1px solid var(--border)', overflow: 'hidden' }}>
        {([null, ...COMPLEETHEID] as const).map((value) => {
          const isActive = compleetheid === value;
          return (
            <button
              aria-pressed={isActive}
              className={`min-h-11 px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0 ${FOCUS_CLASS}`}
              data-testid={`filter-${value ?? 'all'}`}
              key={value ?? 'all'}
              onClick={() => zetPf('completeness', value)}
              style={{ ...knopStijl(isActive), borderRight: value !== 'complete' ? '1px solid var(--border)' : undefined }}
              type="button"
            >
              {t(COMPLEETHEID_LABELS[value ?? 'all']!)}
            </button>
          );
        })}
      </div>
      <FieldTypeFilter onChange={(w) => zetPf(PF.veldtype, w)} value={paginafilterWaarde(paginafilters, PF.veldtype)} />
    </>
  );

  return (
    <div data-testid="product-list" ref={lijstRef} tabIndex={-1}>
      <FilterRejectedMessage
        baseTestId={FILTER_TEST_ID}
        focusNaWissen={lijstRef}
        linkGeweigerd={linkGeweigerd}
        onClear={() => {
          stand.reset();
          onLinkGewist?.();
        }}
      />

      {categories.length > 0 && (
        <div className="mb-4">
          <CategoryTabs
            activeCategory={paginafilterWaarde(paginafilters, PF.categorie)}
            categories={categories}
            incompleteCounts={Object.fromEntries(categories.map((c) => [c.categoryId, c.incompleteCount]))}
            onCategoryChange={(id) => zetPf(PF.categorie, id)}
          />
        </div>
      )}

      <div className="mb-4">
        <Filterbalk context={context} data={{ ...tabel, keuzes: { ...tabel.keuzes, brand: ordenMerken(tabel.keuzes.brand ?? [], stand.filters.brand) } }} lijst={PRODUCT_LIJST} paginafilters={paginafilterRij} stand={stand} />
      </div>

      {/* Geen overflow-hidden op het blok: de filterpopups moeten eruit kunnen steken; de tabel schuift in haar eigen container. */}
      <div
        className="relative rounded-xl"
        style={{
          backgroundColor: 'var(--background)',
          border: '1px solid var(--border-soft)',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        {isFetching && (
          <div className="absolute inset-x-0 top-0 z-10 h-0.5" style={{ backgroundColor: 'var(--primary)', opacity: 0.6, animation: 'pulse 1.5s ease-in-out infinite' }} />
        )}
        {isError ? (
          <ApiErrorMessage error={error} onRetry={() => refetch()} />
        ) : (
          <div className="overflow-x-auto rounded-xl">
            <table className="w-full" style={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 200ms' }}>
              <thead>
                <tr>
                  {ZICHTBARE_KOLOMMEN.map((k) => (
                    <th
                      aria-sort={ariaSort(stand.sortering, k.sleutel)}
                      className={`px-4 py-2.5 ${k.sleutel === 'missingFields' ? 'text-center' : 'text-left'}`}
                      key={k.sleutel}
                      scope="col"
                      style={{ ...KOP_STIJL, width: KOP_BREEDTE[k.sleutel] }}
                    >
                      <SorteerbareKop kolom={k} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} standaard={standaard} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td className="px-4 py-10 text-center" colSpan={ZICHTBARE_KOLOMMEN.length}>
                      <span aria-hidden="true" className="material-symbols-outlined mb-2 block" style={{ fontSize: '32px', color: 'var(--foreground-muted)', opacity: 0.5 }}>
                        inbox
                      </span>
                      <span className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
                        {t('common.noResults')}
                      </span>
                    </td>
                  </tr>
                ) : (
                  products.map((product) => <ProductRow clientId={clientId} key={product.id} product={product} />)
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {tabel.totaal > STANDAARD_PAGINAGROOTTE && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="pagination">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
              {t('common.page')} {tabel.pagina} {t('common.of')} {tabel.paginas} ({tabel.totaal} {t('common.rows')})
            </span>
            <select
              aria-label={t('common.rows')}
              className="rounded-[7px] px-2 py-1 text-xs"
              data-testid="page-size"
              onChange={(e) => stand.zetPaginagrootte(Number(e.target.value))}
              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--background)', color: 'var(--foreground-muted)' }}
              value={paginagrootte}
            >
              {PAGINAGROOTTES.map((n) => (
                <option key={n} value={n}>
                  {n} {t('common.rows')}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-1">
            <button
              className="rounded-[7px] px-3 py-1.5 text-xs font-medium transition-all duration-100 disabled:opacity-40"
              data-testid="page-prev"
              disabled={tabel.pagina <= 1}
              onClick={() => stand.zetPagina(tabel.pagina - 1)}
              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--background)', color: 'var(--foreground-muted)' }}
              type="button"
            >
              {t('common.previous')}
            </button>
            <button
              className="rounded-[7px] px-3 py-1.5 text-xs font-medium transition-all duration-100 disabled:opacity-40"
              data-testid="page-next"
              disabled={tabel.pagina >= tabel.paginas}
              onClick={() => stand.zetPagina(tabel.pagina + 1)}
              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--background)', color: 'var(--foreground-muted)' }}
              type="button"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
